// Sprint 2 — Reorganización de configuración
import { AnimatePresence, motion } from "motion/react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { css, cx } from "styled-system/css";

type ToastKind = "success" | "error" | "info";

type ToastItem = { id: string; kind: ToastKind; message: string };

type ToastApi = {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const MAX_TOASTS = 3;
const DISMISS_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setItems((prev) => {
      const next = [...prev, { id, kind, message }];
      if (next.length > MAX_TOASTS) next.splice(0, next.length - MAX_TOASTS);
      return next;
    });
    const t = setTimeout(() => {
      setItems((prev) => prev.filter((x) => x.id !== id));
      timers.current.delete(id);
    }, DISMISS_MS);
    timers.current.set(id, t);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push("success", m),
      error: (m) => push("error", m),
      info: (m) => push("info", m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className={css({
          position: "fixed",
          bottom: "4",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          display: "flex",
          flexDir: "column",
          gap: "2",
          w: "100%",
          maxW: "28rem",
          px: "4",
          pointerEvents: "none",
        })}
      >
        <AnimatePresence mode="popLayout">
          {items.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
              className={cx(
                css({
                  pointerEvents: "auto",
                  rounded: "lg",
                  px: "4",
                  py: "3",
                  fontSize: "sm",
                  fontWeight: "medium",
                  boxShadow: "0 12px 32px rgba(15,23,42,0.18)",
                  borderWidth: "1px",
                }),
                t.kind === "success"
                  ? css({ bg: "#ecfdf5", color: "#166534", borderColor: "#bbf7d0" })
                  : t.kind === "error"
                    ? css({ bg: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" })
                    : css({ bg: "#f8fafc", color: "#334155", borderColor: "#e2e8f0" }),
              )}
            >
              {t.message}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const c = useContext(ToastContext);
  if (!c) throw new Error("useToast requiere ToastProvider");
  return c;
}
